import React, { memo, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { PandaDocDocumentDocType, PandaDocDocumentOutcome } from 'core/graphql';
import { usePandaDocDocuments } from 'core/hooks';
import { PATHS } from 'router/config';

import * as S from './PandaDocAnalytics.styled';
import { DASH, fmtDate, fmtTTS } from './utils';

const DOC_TYPE_BADGE: Record<
  PandaDocDocumentDocType,
  { variant: string; labelKey: string }
> = {
  [PandaDocDocumentDocType.Student]: {
    variant: 'student',
    labelKey: 'pages.pandadocAnalytics.documents.docTypeStudent',
  },
  [PandaDocDocumentDocType.Trainer]: {
    variant: 'trainer',
    labelKey: 'pages.pandadocAnalytics.documents.docTypeTrainer',
  },
  [PandaDocDocumentDocType.Dofin]: {
    variant: 'dofin',
    labelKey: 'pages.pandadocAnalytics.documents.docTypeDofin',
  },
  [PandaDocDocumentDocType.Other]: {
    variant: 'other',
    labelKey: 'pages.pandadocAnalytics.documents.docTypeOther',
  },
};

const OUTCOME_BADGE: Record<
  PandaDocDocumentOutcome,
  { variant: string; labelKey: string }
> = {
  [PandaDocDocumentOutcome.Signed]: {
    variant: 'signed',
    labelKey: 'pages.pandadocAnalytics.documents.outcomeSigned',
  },
  [PandaDocDocumentOutcome.Active]: {
    variant: 'active',
    labelKey: 'pages.pandadocAnalytics.documents.outcomeActive',
  },
  [PandaDocDocumentOutcome.Lost]: {
    variant: 'lost',
    labelKey: 'pages.pandadocAnalytics.documents.outcomeLost',
  },
};

interface IDocumentsTableProps {
  /** Course filter shared with the analytics queries (`all` → no filter). */
  course: string;
  /** ISO `YYYY-MM-DD` date-created bounds; `null` means unbounded. */
  dateFrom: string | null;
  dateTo: string | null;
}

/** Searchable, cursor-paginated table of PandaDoc documents (tz.md §4.7). */
export const DocumentsTable = memo(
  ({ course, dateFrom, dateTo }: IDocumentsTableProps) => {
    const { t } = useTranslation();
    const history = useHistory();
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    useEffect(() => {
      const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300);
      return () => clearTimeout(timer);
    }, [search]);

    const filters = useMemo(
      () => ({
        course: course === 'all' ? null : course,
        search: debouncedSearch || null,
        dateFrom: dateFrom || null,
        dateTo: dateTo || null,
      }),
      [course, debouncedSearch, dateFrom, dateTo],
    );

    const {
      data,
      totalCount,
      loading,
      permissionDenied,
      hasNextPage,
      operations,
    } = usePandaDocDocuments(filters);

    return (
      <S.Card>
        <S.ListHeader>
          <S.CardTitle>{t('pages.pandadocAnalytics.table.list')}</S.CardTitle>
          <S.CardMeta>
            {t('pages.pandadocAnalytics.table.countOfTotal', {
              data: data.length,
              total: totalCount,
            })}
          </S.CardMeta>
          <S.SearchInput
            type="search"
            placeholder={t('pages.pandadocAnalytics.table.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </S.ListHeader>

        {permissionDenied ? (
          <S.EmptyState>
            {t('pages.pandadocAnalytics.table.noAccess')}
          </S.EmptyState>
        ) : data.length === 0 ? (
          <S.EmptyState>
            {loading
              ? t('pages.pandadocAnalytics.table.loading')
              : t('pages.pandadocAnalytics.table.notFound')}
          </S.EmptyState>
        ) : (
          <>
            <S.Table>
              <thead>
                <tr>
                  <th style={{ width: 110 }}>
                    {t('pages.pandadocAnalytics.table.headerDate')}
                  </th>
                  <th style={{ width: 100 }}>
                    {t('pages.pandadocAnalytics.table.headerType')}
                  </th>
                  <th style={{ width: 110 }}>
                    {t('pages.pandadocAnalytics.table.headerStatus')}
                  </th>
                  <th>{t('pages.pandadocAnalytics.table.headerDocument')}</th>
                  <th style={{ width: 200 }}>
                    {t('pages.pandadocAnalytics.table.headerAgreement')}
                  </th>
                  <th style={{ width: 140 }}>
                    {t('pages.pandadocAnalytics.table.headerCourse')}
                  </th>
                  <th style={{ width: 100 }}>
                    {t('pages.pandadocAnalytics.table.headerTts')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.map((doc) => {
                  const typeBadge = DOC_TYPE_BADGE[doc.docType];
                  const outcomeBadge = OUTCOME_BADGE[doc.outcome];
                  // Phase 13: deep-link to the agreement's group detail
                  // when provisioning created the link, otherwise mark
                  // the row as «not provisioned yet» for visibility.
                  const ag = doc.agreement;
                  const isSigned =
                    doc.outcome === PandaDocDocumentOutcome.Signed;
                  return (
                    <tr key={doc.id}>
                      <S.DateCell>
                        {fmtDate(doc.dateCompleted || doc.dateCreated)}
                      </S.DateCell>
                      <td>
                        <S.Badge variant={typeBadge.variant}>
                          {t(typeBadge.labelKey)}
                        </S.Badge>
                      </td>
                      <td>
                        <S.Badge variant={outcomeBadge.variant}>
                          {t(outcomeBadge.labelKey)}
                        </S.Badge>
                      </td>
                      <S.NameCell>{doc.name}</S.NameCell>
                      <td>
                        {ag && ag.group ? (
                          <button
                            type="button"
                            onClick={() =>
                              history.push(
                                PATHS.groupDetail.replace(':id', ag.group!.id),
                              )
                            }
                            style={{
                              border: 0,
                              background: 'transparent',
                              padding: 0,
                              color: '#2563eb',
                              fontSize: 12,
                              fontWeight: 600,
                              cursor: 'pointer',
                              textAlign: 'left',
                            }}
                          >
                            {ag.agreementTitle}
                            <div style={{ color: '#64748b', fontSize: 11 }}>
                              {ag.group.name}
                            </div>
                          </button>
                        ) : isSigned ? (
                          <span style={{ color: '#94a3b8', fontSize: 12 }}>
                            {t('pages.pandadocAnalytics.table.notProvisioned')}
                          </span>
                        ) : (
                          <span style={{ color: '#cbd5f5' }}>{DASH}</span>
                        )}
                      </td>
                      <td>
                        <S.CourseBadge>{doc.course || DASH}</S.CourseBadge>
                      </td>
                      <S.NumCell>{fmtTTS(doc.ttsHours, t)}</S.NumCell>
                    </tr>
                  );
                })}
              </tbody>
            </S.Table>
            {hasNextPage && (
              <S.LoadMoreButton
                type="button"
                disabled={loading}
                onClick={operations.fetchMore}
              >
                {loading
                  ? t('pages.pandadocAnalytics.table.loading')
                  : t('pages.pandadocAnalytics.table.loadMore')}
              </S.LoadMoreButton>
            )}
          </>
        )}
      </S.Card>
    );
  },
);
